import { useQuery } from '@tanstack/react-query';
import { fetchItems } from '../support/queries';

export interface SeActivityMetric {
  /** Keyword-grouped categories ("POC/POV", "Demo", "Design/Whiteboard" ...). */
  activity_type_counts?: Record<string, number>;
  /** Exact Task.SE_Service_Type__c -> count; absent on a server that predates it. */
  service_type_counts?: Record<string, number>;
  activity_total?: number;
}

export interface SeActivityNode {
  email: string;
  is_manager?: boolean;
  /** This person's own activities. */
  own: SeActivityMetric;
  /** This person plus every descendant. */
  rollup: SeActivityMetric;
  reports?: Record<string, SeActivityNode>;
}

export interface SeActivitySummaryResponse {
  report?: string;
  target_email?: string;
  current_time_filter?: string;
  tree?: SeActivityNode;
}

/**
 * SE activity counts for a leader's org, from `sf/team/se-activity-summary/`: one node per person with
 * `own` and subtree `rollup` counts, nested by direct report. `timeFilter` is `all` or
 * `range:YYYY-MM-DD:YYYY-MM-DD` (the Salesforce tab's time dropdown).
 */
export function useSfTeamActivitySummary(
  email: string | null | undefined,
  timeFilter: string,
  options: { enabled?: boolean } = {},
) {
  const normalizedEmail = String(email || '').toLowerCase() || null;
  return useQuery<SeActivitySummaryResponse | null>({
    queryKey: ['SfTeamActivitySummary', normalizedEmail, timeFilter],
    queryFn: async () => {
      if (!normalizedEmail) return null;
      return fetchItems(
        'sf/team/se-activity-summary/',
        { email: normalizedEmail, current_time_filter: timeFilter },
        { includePageSize: false },
      );
    },
    enabled: !!normalizedEmail && (options.enabled ?? true),
    refetchOnWindowFocus: false,
    staleTime: 30_000,
  });
}
